// The in-house router, client half (ADR-017; ported from spikes/router/src/inhouse/transport.ts). The only
// hand-written code in @radish/api-client: plain fetch, inputs encoded and responses decoded through the contract's
// codecs (so Money arrives as bigint minor units and never as a float), declared errors returned as typed values and
// never thrown. A location-profile client answers `needs_internet` for cloud operations without touching the network.
import { z } from 'zod';
import {
  errorEnvelope, headers, paths, secretPaths, successEnvelope,
  type CommandDef, type ErrorDef, type ErrorOf, type OperationDef, type PlatformError, type QueryDef,
} from '@radish/contracts';

export interface ClientOptions {
  readonly baseUrl: string;
  /** 'location' talks to the location node, which does not serve cloud operations. */
  readonly profile: 'location' | 'cloud';
  readonly headers?: () => Readonly<Record<string, string>>;
  readonly fetch?: typeof fetch;
}

/**
 * A fetch whose every request is aborted when `signal` aborts (a caller's own cancellation: the back office's report
 * queries pass TanStack's signal, B1384), over `base` or the global fetch. A request that already carries a signal of
 * its own is aborted by either until its body is read, failed or cancelled (a reply without a readable body, until the
 * fetch settles); then the joined signal lets go of both, so a long-lived signal gathers no listener per request
 * (#2355 review) and an abort while the body streams still stops it (#2375 review). A request so aborted answers
 * `network_error`.
 */
export function abortingFetch(signal: AbortSignal, base?: typeof fetch): typeof fetch {
  return (input, init) => {
    if (init?.signal == null) return (base ?? fetch)(input, { ...init, signal });
    const joined = eitherSignal(signal, init.signal);
    return (base ?? fetch)(input, { ...init, signal: joined.signal }).then(
      (res) => releasedWithBody(res, joined.release),
      (error: unknown) => { joined.release(); throw error; },
    );
  };
}

/**
 * `res` with `release` called once its body is read to the end, fails or is cancelled. A reply with no body, or a runtime
 * without readable streams (React Native), releases at once. The status, headers and url are the reply's own.
 */
function releasedWithBody(res: Response, release: () => void): Response {
  if (res.body == null || typeof ReadableStream === 'undefined') {
    release();
    return res;
  }
  const reader = res.body.getReader();
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        const chunk = await reader.read();
        if (chunk.done) {
          release();
          controller.close();
        } else controller.enqueue(chunk.value);
      } catch (error) {
        release();
        controller.error(error);
      }
    },
    cancel(reason) {
      release();
      return reader.cancel(reason);
    },
  });
  const wrapped = new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
  Object.defineProperty(wrapped, 'url', { value: res.url });
  return wrapped;
}

/**
 * A signal that aborts when either does, with the first one's reason (no `AbortSignal.any` on every runtime we ship
 * to), and `release`, which takes its listeners off both. Aborting releases them too.
 */
function eitherSignal(a: AbortSignal, b: AbortSignal): { readonly signal: AbortSignal; readonly release: () => void } {
  const none = () => {};
  if (a.aborted) return { signal: a, release: none };
  if (b.aborted) return { signal: b, release: none };
  const both = new AbortController();
  const release = () => {
    a.removeEventListener('abort', fromA);
    b.removeEventListener('abort', fromB);
  };
  const stop = (from: AbortSignal) => () => {
    release();
    both.abort(from.reason);
  };
  const fromA = stop(a);
  const fromB = stop(b);
  a.addEventListener('abort', fromA, { once: true });
  b.addEventListener('abort', fromB, { once: true });
  return { signal: both.signal, release };
}

/** Failures that never reached a declared error: the network is down, or a response that breaks the contract. */
export type TransportError =
  | { readonly code: 'network_error'; readonly retryable: true; readonly details: { readonly message: string } }
  | { readonly code: 'unexpected_response'; readonly retryable: false; readonly details: { readonly status: number } };

export type CallError<E extends readonly ErrorDef[]> = ErrorOf<E> | PlatformError | TransportError;

export type Call<T, E extends readonly ErrorDef[]> =
  | { readonly ok: true; readonly data: T; readonly status: number; readonly replayed: boolean }
  | { readonly ok: false; readonly status: number; readonly error: CallError<E> };

export type CommandResult<D extends CommandDef> = Call<z.output<D['output']>, D['errors']>;
export type QueryResult<D extends QueryDef> = Call<z.output<D['output']>, D['errors']>;

/** Retries send the same `commandId`, so a command can never apply twice. */
export interface CommandCall {
  readonly commandId: string;
  readonly dryRun?: boolean;
}

const envelopes = new WeakMap<OperationDef, { ok: z.ZodType; err: z.ZodType }>();
function envelopesOf(def: OperationDef) {
  let hit = envelopes.get(def);
  if (hit === undefined) {
    hit = { ok: successEnvelope(def.output), err: errorEnvelope(def.errors) };
    envelopes.set(def, hit);
  }
  return hit;
}

function failure<E extends readonly ErrorDef[]>(status: number, error: CallError<E>): Call<never, E> {
  return { ok: false, status, error };
}

/**
 * Whether `send` failed before a response. Told apart by shape, not `instanceof Response`: on React Native with Expo the
 * global `fetch` answers with its own Response class, not the global `Response`, so an `instanceof` check took every
 * answer for a failure (the register's Order Lookup on the emulator, lane D6 2026-10-13).
 */
function isTransportError(r: Response | TransportError): r is TransportError {
  return typeof (r as { code?: unknown }).code === 'string';
}

async function send(options: ClientOptions, path: string, init: RequestInit): Promise<Response | TransportError> {
  try {
    const doFetch = options.fetch ?? fetch;
    return await doFetch(options.baseUrl + path, { ...init, headers: { ...options.headers?.(), ...(init.headers as Record<string, string>) } });
  } catch (error) {
    return { code: 'network_error', retryable: true, details: { message: String(error) } };
  }
}

async function decode<T, E extends readonly ErrorDef[]>(res: Response, def: OperationDef): Promise<Call<T, E>> {
  const { ok, err } = envelopesOf(def);
  const body: unknown = await res.json().catch(() => undefined);
  if (res.ok) {
    const parsed = ok.safeDecode(body);
    if (parsed.success) return { ok: true, data: (parsed.data as { data: T }).data, status: res.status, replayed: res.headers.get(headers.replayed) === 'true' };
  } else {
    const parsed = err.safeDecode(body);
    if (parsed.success) return failure<E>(res.status, (parsed.data as { error: CallError<E> }).error);
  }
  return failure<E>(res.status, { code: 'unexpected_response', retryable: false, details: { status: res.status } });
}

function encodeInput(def: OperationDef, input: unknown): { ok: true; wire: unknown } | { ok: false; error: PlatformError } {
  const encoded = z.safeEncode(def.input, input);
  if (encoded.success) return { ok: true, wire: encoded.data };
  const issues = encoded.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }));
  return { ok: false, error: { code: 'validation_failed', retryable: false, details: { issues } } };
}

function unreachable(options: ClientOptions, def: OperationDef): PlatformError | undefined {
  if (options.profile === 'cloud' || def.availability === 'location') return undefined;
  return { code: 'needs_internet', retryable: true, details: { operation: def.name } };
}

export async function callCommand<D extends CommandDef>(
  options: ClientOptions, def: D, input: z.output<D['input']>, call: CommandCall,
): Promise<CommandResult<D>> {
  const offline = unreachable(options, def);
  if (offline !== undefined) return failure<D['errors']>(0, offline);
  const encoded = encodeInput(def, input);
  if (!encoded.ok) return failure<D['errors']>(0, encoded.error);
  const res = await send(options, paths.command(def.name), {
    method: 'POST',
    headers: { 'content-type': 'application/json', [headers.idempotencyKey]: call.commandId },
    body: JSON.stringify({ commandId: call.commandId, input: encoded.wire, ...(call.dryRun ? { dryRun: true } : {}) }),
  });
  if (isTransportError(res)) return failure<D['errors']>(0, res);
  return decode(res, def);
}

export async function callQuery<D extends QueryDef>(options: ClientOptions, def: D, input: z.output<D['input']>): Promise<QueryResult<D>> {
  const offline = unreachable(options, def);
  if (offline !== undefined) return failure<D['errors']>(0, offline);
  const encoded = encodeInput(def, input);
  if (!encoded.ok) return failure<D['errors']>(0, encoded.error);
  // D7: a query with a secret input (a PIN, a gift-card number) goes in a POST body, never a URL.
  const res = secretPaths(def.input).length > 0
    ? await send(options, paths.query(def.name), { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ input: encoded.wire }) })
    : await send(options, `${paths.query(def.name)}?input=${encodeURIComponent(JSON.stringify(encoded.wire))}`, { method: 'GET' });
  if (isTransportError(res)) return failure<D['errors']>(0, res);
  return decode(res, def);
}
